import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Flex, Text, Button, Badge, chakra } from '@chakra-ui/react';
import { CheckCircle, ChevronDown, ChevronUp, X, Clock, ArrowRight, Info } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader, NavBar, DurationStepper } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { EventImage } from '@/components/common/EventImage';
import {
  type Task,
  type TaskStatus,
  formatDeadlineWithWeekday,
  isOverdue,
  sortTasksForUpNext,
  normalizeTaskRow,
} from './volunteerTaskUtils';
import { TaskInfoModal } from './TaskInfoModal';
import { formatProfileName } from '@/utils/profileHelpers';
import { insertNotification } from '@/utils/notificationInsert';
import { fetchProfileDisplayName } from '@/utils/notificationHelpers';
import { minutesToHours, validateDuration } from '@/utils/volunteerHours';

/** Tab filters (excludes in-progress-only view; those tasks still show under All). */
type StatusTabFilter = 'all' | 'pending' | 'completed';

const STATUS_FILTERS: { id: StatusTabFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
];

const STATUS_COLORS: Record<TaskStatus, string> = {
  pending: '#FF9500',
  in_progress: '#007AFF',
  completed: '#34C759',
};

/** Max tasks in the "Up next" strip (plan: 3–5) */
const UP_NEXT_MAX = 5;

export function VolunteerTasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusEventId = searchParams.get('event');
  const focusTaskId = searchParams.get('task');
  const lastScrolledEventRef = useRef<string | null>(null);
  const lastFocusedTaskRef = useRef<string | null>(null);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [eventMap, setEventMap] = useState<Map<string, { id: string; name: string; image: string | null; location: string | null }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusTabFilter>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const userToggledCollapse = useRef<Set<string>>(new Set());

  const [pendingCompleteTask, setPendingCompleteTask] = useState<Task | null>(null);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [descInput, setDescInput] = useState('');
  const [submittingHours, setSubmittingHours] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [hoursAcceptedTaskIds, setHoursAcceptedTaskIds] = useState<Set<string>>(new Set());
  const [profileNameById, setProfileNameById] = useState<Map<string, string>>(new Map());
  const [infoTask, setInfoTask] = useState<Task | null>(null);
  const [revertError, setRevertError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select(
          'id, event_id, title, description, status, deadline, hours_submitted, created_at, assigned_volunteers, assigned_departments',
        )
        .order('deadline', { ascending: true, nullsFirst: false });

      if (taskError) throw taskError;
      const rawRows = (taskData ?? []) as Record<string, unknown>[];
      const rawTasks = rawRows.map((row) => normalizeTaskRow(row));

      const eventIdsAll = [...new Set(rawTasks.map((t) => t.event_id))];
      const endedEventIds = new Set<string>();
      let eventsData: {
        id: string;
        name: string | null;
        image: string | null;
        location: string | null;
        is_active: boolean | null;
      }[] = [];

      if (eventIdsAll.length > 0) {
        const { data: evRows, error: evErr } = await supabase
          .from('events')
          .select('id, name, image, location, is_active')
          .in('id', eventIdsAll);
        if (!evErr && evRows) {
          eventsData = evRows;
          for (const ev of evRows) {
            if (ev.is_active === false) endedEventIds.add(ev.id);
          }
        }
      }

      const visibleTasks = rawTasks.filter((t) => !endedEventIds.has(t.event_id));
      setTasks(visibleTasks);

      const assigneeIds = [
        ...new Set(visibleTasks.flatMap((t) => t.assigned_volunteers ?? []).filter(Boolean)),
      ];
      if (assigneeIds.length > 0) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', assigneeIds);
        const next = new Map<string, string>();
        for (const p of profileData ?? []) {
          next.set(
            p.id,
            formatProfileName(p.first_name, p.last_name, 'Volunteer'),
          );
        }
        setProfileNameById(next);
      } else {
        setProfileNameById(new Map());
      }

      const taskIds = visibleTasks.map((t) => t.id);
      if (taskIds.length > 0) {
        const { data: acceptedRows } = await supabase
          .from('accepted_volunteer_hours')
          .select('task_id')
          .eq('volunteer_id', user.id)
          .not('task_id', 'is', null)
          .in('task_id', taskIds);
        setHoursAcceptedTaskIds(
          new Set(
            (acceptedRows ?? [])
              .map((r) => r.task_id)
              .filter((id): id is string => typeof id === 'string' && id.length > 0),
          ),
        );
      } else {
        setHoursAcceptedTaskIds(new Set());
      }

      const visibleEventIds = new Set(visibleTasks.map((t) => t.event_id));
      if (visibleEventIds.size > 0) {
        const map = new Map<string, { id: string; name: string; image: string | null; location: string | null }>();
        for (const ev of eventsData) {
          if (!visibleEventIds.has(ev.id)) continue;
          map.set(ev.id, {
            id: ev.id,
            name: ev.name ?? 'Unknown event',
            image: ev.image ?? null,
            location: ev.location ?? null,
          });
        }
        setEventMap(map);
      } else {
        setEventMap(new Map());
      }
    } catch {
      setError('Could not load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && user?.id) void fetchData();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchData, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const membershipChannel = supabase
      .channel(`volunteer_tasks_membership:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'volunteers',
          filter: `volunteer_id=eq.${user.id}`,
        },
        () => {
          void fetchData();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(membershipChannel);
    };
  }, [fetchData, user?.id]);

  useEffect(() => {
    if (!focusEventId || loading) return;
    if (!tasks.some((t) => t.event_id === focusEventId)) return;
    setStatusFilter('all');
  }, [focusEventId, loading, tasks]);

  // Default: collapsed when all tasks for that event are done; expanded when any are open.
  useEffect(() => {
    if (loading || tasks.length === 0) return;
    setCollapsedGroups((prev) => {
      const byEvent = new Map<string, Task[]>();
      for (const t of tasks) {
        if (!byEvent.has(t.event_id)) byEvent.set(t.event_id, []);
        byEvent.get(t.event_id)!.push(t);
      }
      const next = new Set(prev);
      for (const [eid, list] of byEvent) {
        if (userToggledCollapse.current.has(eid)) continue;
        const allDone = list.length > 0 && list.every((x) => x.status === 'completed');
        if (allDone) next.add(eid);
        else next.delete(eid);
      }
      return next;
    });
  }, [tasks, loading]);

  const handleToggleTap = (task: Task) => {
    if (task.status === 'completed') {
      if (hoursAcceptedTaskIds.has(task.id)) return;
      markIncomplete(task);
    } else {
      setPendingCompleteTask(task);
      setDurationMinutes(60);
      setDescInput('');
      setHoursError(null);
    }
  };

  const markIncomplete = async (task: Task) => {
    if (hoursAcceptedTaskIds.has(task.id) || !user?.id) return;
    setRevertError(null);
    const snapshot = { status: task.status, hours_submitted: task.hours_submitted };
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: 'pending', hours_submitted: false } : t)),
    );

    // Remove pending org hour request so the org no longer sees this until the volunteer submits again
    const { error: delErr } = await supabase
      .from('volunteer_activities')
      .delete()
      .eq('task_id', task.id)
      .eq('volunteer_id', user.id);

    if (delErr) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, status: snapshot.status, hours_submitted: snapshot.hours_submitted }
            : t,
        ),
      );
      setRevertError(delErr.message || 'Could not withdraw your hour request.');
      return;
    }

    const { error: err } = await supabase
      .from('tasks')
      .update({ status: 'pending', completed_at: null, hours_submitted: false })
      .eq('id', task.id);

    if (err) {
      setRevertError(err.message || 'Could not mark the task incomplete.');
      void fetchData();
    }
  };

  const handleSubmitHours = async () => {
    if (!pendingCompleteTask || !user?.id) return;
    const validationError = validateDuration(durationMinutes);
    if (validationError) {
      setHoursError(validationError);
      return;
    }
    setSubmittingHours(true);
    setHoursError(null);
    try {
      const now = new Date();
      const end = new Date(now.getTime() + durationMinutes * 60_000);

      const { data: activity, error: actErr } = await supabase
        .from('volunteer_activities')
        .insert({
          volunteer_id: user.id,
          event_id: pendingCompleteTask.event_id,
          task_id: pendingCompleteTask.id,
          start_time: now.toISOString(),
          end_time: end.toISOString(),
          hours: minutesToHours(durationMinutes),
          description: descInput.trim() || null,
        })
        .select('id')
        .single();

      if (actErr) {
        setHoursError(actErr.message || 'Failed to submit hours. Try again.');
        return;
      }

      const { error: taskErr } = await supabase
        .from('tasks')
        .update({
          status: 'completed',
          completed_at: now.toISOString(),
          hours_submitted: true,
        })
        .eq('id', pendingCompleteTask.id);

      if (taskErr) {
        setHoursError(taskErr.message || 'Failed to mark task complete. Try again.');
        return;
      }

      if (activity?.id) {
        const { data: eventRow } = await supabase
          .from('events')
          .select('id, name, created_by')
          .eq('id', pendingCompleteTask.event_id)
          .maybeSingle();
        const orgId = eventRow?.created_by;
        if (orgId) {
          const volunteerName = await fetchProfileDisplayName(user.id);
          const taskTitle = pendingCompleteTask.title.trim() || 'a task';
          const eventLabel = eventRow?.name?.trim() || 'your event';
          insertNotification({
            user_id: orgId,
            title: 'Task submitted for review',
            message: `${volunteerName} submitted hours for review on "${taskTitle}" in ${eventLabel}.`,
            type: 'task_submission_pending',
            data: {
              activity_id: activity.id,
              task_id: pendingCompleteTask.id,
              event_id: pendingCompleteTask.event_id,
              volunteer_id: user.id,
              task_title: taskTitle,
              event_name: eventLabel,
              volunteer_name: volunteerName,
            },
            read: false,
          });
        }
      }

      setTasks((prev) =>
        prev.map((t) =>
          t.id === pendingCompleteTask.id
            ? { ...t, status: 'completed', hours_submitted: true }
            : t,
        ),
      );
      setPendingCompleteTask(null);
    } catch (e: unknown) {
      setHoursError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setSubmittingHours(false);
    }
  };

  const toggleGroup = (eventId: string) => {
    userToggledCollapse.current.add(eventId);
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };

  const filterCounts = useMemo(() => {
    const t = tasks;
    return {
      all: t.length,
      pending: t.filter((x) => x.status === 'pending').length,
      completed: t.filter((x) => x.status === 'completed').length,
    };
  }, [tasks]);

  const openCount = useMemo(
    () => tasks.filter((t) => t.status !== 'completed').length,
    [tasks],
  );
  const completedCount = filterCounts.completed;
  const totalCount = tasks.length;

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      return true;
    });
  }, [tasks, statusFilter]);

  const upNextCandidates = useMemo(() => {
    const incomplete = filteredTasks.filter((t) => t.status !== 'completed');
    return sortTasksForUpNext(incomplete).slice(0, UP_NEXT_MAX);
  }, [filteredTasks]);

  const showUpNext =
    upNextCandidates.length > 0 &&
    (statusFilter === 'all' || statusFilter === 'pending');

  const groups = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of filteredTasks) {
      if (!map.has(t.event_id)) map.set(t.event_id, []);
      map.get(t.event_id)!.push(t);
    }
    return [...map.entries()].map(([eventId, eventTasks]) => ({
      eventId,
      event: eventMap.get(eventId),
      tasks: eventTasks,
    }));
  }, [filteredTasks, eventMap]);

  useEffect(() => {
    if (!focusEventId || loading) return;
    if (!groups.some((g) => g.eventId === focusEventId)) return;
    if (lastScrolledEventRef.current === focusEventId) return;
    lastScrolledEventRef.current = focusEventId;

    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.delete(focusEventId);
      return next;
    });
    userToggledCollapse.current.add(focusEventId);

    const t = window.setTimeout(() => {
      document.getElementById(`volunteer-tasks-event-${focusEventId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
    return () => window.clearTimeout(t);
  }, [focusEventId, loading, groups]);

  useEffect(() => {
    if (!focusTaskId || loading) return;
    const task = tasks.find((t) => t.id === focusTaskId);
    if (!task) return;
    if (lastFocusedTaskRef.current === focusTaskId) return;
    lastFocusedTaskRef.current = focusTaskId;

    const eventId = focusEventId ?? task.event_id;
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
    userToggledCollapse.current.add(eventId);

    const t = window.setTimeout(() => {
      document.getElementById(`volunteer-tasks-task-${focusTaskId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setInfoTask(task);
    }, focusEventId ? 280 : 150);
    return () => window.clearTimeout(t);
  }, [focusTaskId, focusEventId, loading, tasks]);

  const pendingCompleteEventName = pendingCompleteTask
    ? eventMap.get(pendingCompleteTask.event_id)?.name ?? 'Event'
    : '';

  return (
    <Box
      minH="100vh"
      bg="white"
      pl={{ base: 0, lg: 'var(--sidebar-width)' }}
      pb={{ base: 'var(--mobile-bottom-safe)', lg: '24px' }}
      transition="padding-left 0.3s ease-in-out"
    >
      <Box maxW="800px" mx="auto" px="5" py="5">
        <PageHeader
          title="My tasks"
          showBack
          onBack={() => navigate('/organizations')}
          backLabel="Back"
          showMobileLogo={false}
        />
        {revertError && (
          <Text color="volyx.error" fontSize="sm" mb="3" m="0" role="alert">
            {revertError}
          </Text>
        )}

        {/* Summary */}
        {!loading && !error && totalCount > 0 && (
          <Box mb="4">
            <Text fontSize="lg" lineHeight="tall" color="volyx.text" m="0">
              <Text as="span" fontWeight="bold">
                {openCount} open
              </Text>
              {completedCount > 0 && (
                <>
                  <Text as="span" color="volyx.textSecondary">
                    {' '}
                    · {completedCount} completed
                  </Text>
                </>
              )}
              <Text as="span" color="volyx.textSecondary">
                {' '}
                · {totalCount} total
              </Text>
            </Text>
            <Box
              mt="3"
              h="14px"
              bg="volyx.lightGray"
              borderRadius="full"
              overflow="hidden"
              maxW="100%"
            >
              <Box
                h="full"
                w={`${totalCount ? Math.round((completedCount / totalCount) * 100) : 0}%`}
                bg="volyx.success"
                borderRadius="full"
                transition="width 0.35s ease"
              />
            </Box>
          </Box>
        )}

        <Box mb="4">
          <Flex gap="2" flexWrap="wrap" role="tablist" aria-label="Task status">
            {STATUS_FILTERS.map((f) => {
              const count =
                f.id === 'all'
                  ? filterCounts.all
                  : filterCounts[f.id];
              const active = statusFilter === f.id;
              return (
                <chakra.button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  cursor="pointer"
                  px="3"
                  py="1.5"
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="semibold"
                  fontFamily="body"
                  borderWidth="1.5px"
                  borderColor={active ? 'volyx.primary' : 'volyx.mediumGray'}
                  bg={active ? 'volyx.primary' : 'white'}
                  color={active ? 'white' : 'volyx.text'}
                  transition="all 0.15s"
                  onClick={() => setStatusFilter(f.id)}
                  _hover={{
                    borderColor: active ? 'volyx.primary' : 'volyx.textSecondary',
                  }}
                >
                  {f.label} · {count}
                </chakra.button>
              );
            })}
          </Flex>
        </Box>

        {loading ? (
          <Flex direction="column" gap="4">
            {[0, 1, 2].map((k) => (
              <Box key={k} h="88px" bg="volyx.lightGray" borderRadius="xl" />
            ))}
          </Flex>
        ) : error ? (
          <Box textAlign="center" py="12">
            <Text color="volyx.error" fontSize="sm">
              {error}
            </Text>
            <Button mt="3" size="sm" onClick={fetchData}>
              Retry
            </Button>
          </Box>
        ) : groups.length === 0 ? (
          <Box textAlign="center" py="16" px="6" bg="volyx.lightGray" borderRadius="24px">
            <Text fontSize="3xl" mb="3">
              ✅
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="volyx.text" mb="1">
              {statusFilter === 'all'
                ? 'No tasks assigned yet'
                : 'No tasks match your filters'}
            </Text>
            <Text fontSize="sm" color="volyx.textSecondary">
              {statusFilter === 'all'
                ? "You'll see your tasks here when an organization assigns them to you."
                : 'Try changing the filter.'}
            </Text>
          </Box>
        ) : (
          <Flex direction="column" gap="8">
            {showUpNext && (
              <Box as="section" aria-labelledby="up-next-heading">
                <Text
                  id="up-next-heading"
                  as="h2"
                  fontSize="sm"
                  fontWeight="bold"
                  color="volyx.textSecondary"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb="3"
                  m="0"
                >
                  Up next
                </Text>
                <Flex
                  gap="4"
                  overflowX="auto"
                  pb="2"
                  px="0.5"
                  pt="0.5"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {upNextCandidates.map((task) => {
                    const overdue = isOverdue(task.deadline, task.status);
                    return (
                      <Box
                        key={task.id}
                        id={`volunteer-tasks-task-${task.id}`}
                        position="relative"
                        minW="288px"
                        maxW="320px"
                        flexShrink={0}
                        bg="linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)"
                        borderRadius="2xl"
                        border="1px solid"
                        borderColor="volyx.mediumGray"
                        borderLeftWidth="5px"
                        borderLeftColor={overdue ? 'volyx.error' : 'volyx.action'}
                        boxShadow="0 2px 16px rgba(31, 41, 50, 0.06)"
                        overflow="hidden"
                        transition="border-color 0.2s ease, box-shadow 0.2s ease"
                        _hover={{
                          boxShadow: '0 6px 24px rgba(31, 41, 50, 0.1)',
                          borderColor: 'volyx.textSecondary',
                        }}
                      >
                        <chakra.button
                          type="button"
                          position="absolute"
                          top="3"
                          right="3"
                          zIndex={1}
                          w="36px"
                          h="36px"
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg="white"
                          border="1px solid"
                          borderColor="volyx.mediumGray"
                          color="volyx.textSecondary"
                          cursor="pointer"
                          boxShadow="0 1px 4px rgba(0,0,0,0.06)"
                          _hover={{ bg: 'volyx.lightGray', color: 'volyx.primary' }}
                          aria-label="Task details"
                          onClick={() => setInfoTask(task)}
                        >
                          <Info size={18} aria-hidden />
                        </chakra.button>
                        <Box p="4" pb="3" pr="14">
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="volyx.text"
                            m="0"
                            mb="3"
                            lineHeight="tall"
                            css={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {task.title}
                          </Text>

                          <Box h="1px" bg="volyx.mediumGray" opacity={0.45} mb="3" mt="1" />

                          {task.deadline && (
                            <Flex align="center" gap="1.5" minW="0" mb="1">
                              <Box as={Clock} boxSize="15px" color={overdue ? 'volyx.error' : 'volyx.textSecondary'} flexShrink={0} aria-hidden />
                              <Text
                                fontSize="sm"
                                fontWeight={overdue ? 'semibold' : 'medium'}
                                color={overdue ? 'volyx.error' : 'volyx.text'}
                                m="0"
                              >
                                {overdue ? 'Overdue · ' : 'Due '}
                                {formatDeadlineWithWeekday(task.deadline)}
                              </Text>
                            </Flex>
                          )}
                        </Box>

                        <Box px="4" pb="4" pt="0">
                          <chakra.button
                            type="button"
                            w="full"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap="2"
                            py="2.5"
                            px="4"
                            borderRadius="xl"
                            bg="volyx.primary"
                            color="white"
                            fontSize="sm"
                            fontWeight="semibold"
                            fontFamily="body"
                            border="none"
                            cursor="pointer"
                            transition="background 0.15s ease, transform 0.15s ease"
                            _hover={{
                              bg: 'volyx.action',
                              transform: 'translateY(-1px)',
                            }}
                            _active={{ transform: 'translateY(0)' }}
                            onClick={() => handleToggleTap(task)}
                          >
                            Log hours and complete
                            <Box as={ArrowRight} boxSize="16px" aria-hidden />
                          </chakra.button>
                        </Box>
                      </Box>
                    );
                  })}
                </Flex>
              </Box>
            )}

            <Box>
              <Text
                as="h2"
                fontSize="sm"
                fontWeight="bold"
                color="volyx.textSecondary"
                textTransform="uppercase"
                letterSpacing="wider"
                mb="3"
                m="0"
              >
                By event
              </Text>
              <Flex direction="column" gap="5">
                {groups.map(({ eventId, event, tasks: groupTasks }) => {
                  const isCollapsed = collapsedGroups.has(eventId);
                  const pendingCount = groupTasks.filter((t) => t.status !== 'completed').length;
                  return (
                    <Box
                      key={eventId}
                      id={`volunteer-tasks-event-${eventId}`}
                      scrollMarginTop="88px"
                      borderRadius="2xl"
                      border="1px solid"
                      borderColor="volyx.mediumGray"
                      overflow="hidden"
                      boxShadow="0 2px 10px rgba(0,0,0,0.04)"
                    >
                      <chakra.button
                        type="button"
                        w="full"
                        display="flex"
                        alignItems="stretch"
                        gap="3"
                        p="4"
                        bg="volyx.bg"
                        border="none"
                        cursor="pointer"
                        textAlign="left"
                        onClick={() => toggleGroup(eventId)}
                        _hover={{ bg: 'volyx.lightGray' }}
                      >
                        <Box
                          w="56px"
                          h="56px"
                          borderRadius="xl"
                          overflow="hidden"
                          flexShrink={0}
                          bg="volyx.lightGray"
                        >
                          {event?.image ? (
                            <EventImage stored={event.image} w="full" h="full" objectFit="cover" />
                          ) : (
                            <Flex w="full" h="full" align="center" justify="center" fontSize="xs" fontWeight="bold" color="volyx.primary">
                              {(event?.name || 'E')[0].toUpperCase()}
                            </Flex>
                          )}
                        </Box>
                        <Flex flex="1" minW="0" direction="column" justify="center" gap="0.5">
                          <Text fontWeight="bold" fontSize="md" color="volyx.text" m="0" lineClamp={2}>
                            {event?.name ?? 'Event'}
                          </Text>
                          {event?.location ? (
                            <Text fontSize="xs" color="volyx.textSecondary" m="0" lineClamp={1}>
                              {event.location}
                            </Text>
                          ) : null}
                          <Text fontSize="xs" color="volyx.textSecondary" m="0">
                            {pendingCount > 0
                              ? `${pendingCount} open task${pendingCount !== 1 ? 's' : ''}`
                              : 'All tasks done'}
                          </Text>
                        </Flex>
                        <Flex align="center" color="volyx.textSecondary" flexShrink={0}>
                          {isCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                        </Flex>
                      </chakra.button>

                      {!isCollapsed && (
                        <Box p="4" pt="0" bg="white">
                          <Flex direction="column" gap="3">
                            {groupTasks.map((task) => {
                              const overdue = isOverdue(task.deadline, task.status);
                              const hoursLocked =
                                task.status === 'completed' && hoursAcceptedTaskIds.has(task.id);
                              const isDone = task.status === 'completed';
                              return (
                                <Box
                                  key={task.id}
                                  id={`volunteer-tasks-task-${task.id}`}
                                  bg={isDone ? 'volyx.lightGray' : 'white'}
                                  borderRadius="xl"
                                  border="1px solid"
                                  borderColor={overdue ? 'volyx.error' : 'volyx.mediumGray'}
                                  p="4"
                                  boxShadow={isDone ? 'none' : '0 1px 6px rgba(0,0,0,0.05)'}
                                  transition="box-shadow 0.15s"
                                >
                                  <Flex align="flex-start" gap="3" wrap="wrap">
                                    <Flex align="center" pt="0.5" flexShrink={0}>
                                      {task.status === 'completed' ? (
                                        <CheckCircle size={22} color={STATUS_COLORS.completed} aria-hidden />
                                      ) : (
                                        <Box as={Clock} boxSize="22px" color="volyx.textSecondary" aria-hidden />
                                      )}
                                    </Flex>

                                    <Box flex="1" minW="0">
                                      <Flex align="flex-start" justify="space-between" gap="2" mb="1" flexWrap="wrap">
                                        <Box flex="1" minW="0">
                                          <Flex align="center" gap="2" flexWrap="wrap">
                                            <Text
                                              m="0"
                                              fontSize="sm"
                                              fontWeight="semibold"
                                              color={isDone ? 'volyx.textSecondary' : 'volyx.text'}
                                              overflow="hidden"
                                              textOverflow="ellipsis"
                                            >
                                              {task.title}
                                            </Text>
                                            {isDone && (
                                              <Badge size="sm" colorPalette="green" variant="subtle">
                                                Done
                                              </Badge>
                                            )}
                                          </Flex>
                                        </Box>
                                        <Badge
                                          px="2"
                                          py="0.5"
                                          borderRadius="full"
                                          bg="volyx.lightGray"
                                          borderWidth="1.5px"
                                          borderColor={STATUS_COLORS[task.status]}
                                          fontSize="10px"
                                          fontWeight="semibold"
                                          textTransform="capitalize"
                                          color={STATUS_COLORS[task.status]}
                                          flexShrink={0}
                                        >
                                          {task.status.replace('_', ' ')}
                                        </Badge>
                                      </Flex>

                                      {task.description && (
                                        <Text m="0" fontSize="xs" color="volyx.textSecondary" mb="1">
                                          {task.description}
                                        </Text>
                                      )}

                                      {task.deadline && (
                                        <Text
                                          m="0"
                                          fontSize="xs"
                                          color={overdue ? 'volyx.error' : 'volyx.mediumGray'}
                                          fontWeight={overdue ? 'semibold' : 'normal'}
                                          mb="1"
                                        >
                                          {overdue ? 'Overdue · ' : 'Due '}
                                          {formatDeadlineWithWeekday(task.deadline)}
                                        </Text>
                                      )}

                                      {isDone && hoursLocked && (
                                        <Flex align="center" gap="1.5" mt="1">
                                          <Box w="6px" h="6px" borderRadius="full" bg="volyx.success" flexShrink={0} />
                                          <Text m="0" fontSize="xs" color="volyx.textSecondary">
                                            Hours confirmed
                                          </Text>
                                        </Flex>
                                      )}
                                      {isDone && !hoursLocked && task.hours_submitted && (
                                        <Flex align="center" gap="1.5" mt="1">
                                          <Box w="6px" h="6px" borderRadius="full" bg="#FF9500" flexShrink={0} />
                                          <Text m="0" fontSize="xs" color="volyx.textSecondary">
                                            Hours submitted · pending review
                                          </Text>
                                        </Flex>
                                      )}
                                    </Box>

                                    <Flex
                                      direction={{ base: 'column', sm: 'row' }}
                                      gap="2"
                                      align={{ base: 'stretch', sm: 'center' }}
                                      w={{ base: 'full', sm: 'auto' }}
                                      ml={{ base: 0, sm: 'auto' }}
                                    >
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        px="2"
                                        minW="40px"
                                        aria-label="Task details"
                                        onClick={() => setInfoTask(task)}
                                      >
                                        <Info size={18} aria-hidden />
                                      </Button>
                                      {isDone ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={hoursLocked}
                                          onClick={() => handleToggleTap(task)}
                                        >
                                          Mark incomplete
                                        </Button>
                                      ) : (
                                        <Button
                                          size="sm"
                                          bg="volyx.action"
                                          color="white"
                                          _hover={{ opacity: 0.92 }}
                                          onClick={() => handleToggleTap(task)}
                                        >
                                          Complete
                                        </Button>
                                      )}
                                    </Flex>
                                  </Flex>
                                </Box>
                              );
                            })}
                          </Flex>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          </Flex>
        )}
      </Box>

      <NavBar />

      {pendingCompleteTask && (
        <Box
          position="fixed"
          inset="0"
          bg="rgba(0,0,0,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1000}
          px="4"
          onClick={() => !submittingHours && setPendingCompleteTask(null)}
        >
          <Box
            bg="white"
            borderRadius="2xl"
            w="full"
            maxW="380px"
            boxShadow="0 16px 56px rgba(0,0,0,0.18)"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Flex
              px="5"
              py="4"
              justifyContent="space-between"
              alignItems="flex-start"
              borderBottom="1px solid"
              borderColor="volyx.borderLight"
              gap="3"
            >
              <Box minW="0">
                <Text fontSize="lg" fontWeight="bold" color="volyx.primary" m="0">
                  Log your time
                </Text>
                <Text fontSize="xs" color="volyx.textSecondary" m="0" mt="1">
                  {pendingCompleteEventName}
                </Text>
              </Box>
              <chakra.button
                type="button"
                bg="volyx.lightGray"
                border="none"
                cursor="pointer"
                w="32px"
                h="32px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                _hover={{ bg: 'volyx.mediumGray' }}
                onClick={() => !submittingHours && setPendingCompleteTask(null)}
                aria-label="Close"
              >
                <X size={16} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
              </chakra.button>
            </Flex>

            <Box p="5">
              <Text fontSize="sm" color="volyx.textSecondary" mb="4" m="0">
                Task:{' '}
                <Text as="span" fontWeight="semibold" color="volyx.text">
                  {pendingCompleteTask.title}
                </Text>
                . How long did this take? Your organization will review and accept your hours.
              </Text>

              <Box mb="4">
                <DurationStepper value={durationMinutes} onChange={setDurationMinutes} />
              </Box>

              <Box mb="4">
                <Text fontSize="xs" color="volyx.textSecondary" mb="1" m="0">
                  Note (optional)
                </Text>
                <textarea
                  placeholder="Anything the org should know…"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '72px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid var(--chakra-colors-volyx-borderLight)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    background: 'var(--chakra-colors-volyx-lightGray)',
                    outline: 'none',
                    resize: 'vertical',
                    color: 'var(--chakra-colors-volyx-primary)',
                  }}
                />
              </Box>

              {hoursError && (
                <Text fontSize="xs" color="volyx.error" mb="3" m="0">
                  {hoursError}
                </Text>
              )}

              <Flex gap="2">
                <Button
                  flex={1}
                  variant="outline"
                  onClick={() => !submittingHours && setPendingCompleteTask(null)}
                  disabled={submittingHours}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  bg="volyx.action"
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  loading={submittingHours}
                  onClick={handleSubmitHours}
                >
                  Submit
                </Button>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}

      <TaskInfoModal
        task={infoTask}
        open={!!infoTask}
        onClose={() => setInfoTask(null)}
        currentUserId={user?.id ?? ''}
        profileNameById={profileNameById}
      />
    </Box>
  );
}
